/**
 * @fileoverview A portal that controls the tree actions in the DirectoryNodeContainer component.
 *
 * @author Justin Mancusi (jmancusi@yext.com)
 */

import {TreeActionsMode} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/constants';
import {ButtonActions} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/actions/ButtonActions';
import {MenuActions} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/actions/MenuActions';
import {TreeNodeShape} from '/src/com/yext/sitesgitstorm/js/src/editor/tree/nodes/shapesJsDoc';

/**
 * @param {{
 *   node: ?TreeNodeShape,
 *   mode: string,
 *   containingRef: ({current: {getBoundingClientRect: !Function}} | undefined),
 *   onAddOrUploadFiles: (function(string):undefined | undefined),
 *   onAddDirectory: (function(string):undefined | undefined),
 *   onDeleteFile: (function(!TreeNodeShape):undefined | undefined),
 *   onDeleteDirectory: (function(string):undefined | undefined),
 *   onRenameFile: (function(!TreeNodeShape):undefined | undefined),
 *   onRenameDirectory: (function(string):undefined | undefined),
 * }} props
 * @return {React.ReactNode}
 */
export const TreeActionsPortal = (
  {
    node,
    mode,
    onAddOrUploadFiles,
    onAddDirectory,
    onRenameFile,
    onRenameDirectory,
    onDeleteFile,
    onDeleteDirectory,
    containingRef,
  },
) => {
  const [top, setTop] = React.useState(undefined);
  const [left, setLeft] = React.useState(undefined);

  const animationFrameId = React.useRef(undefined);

  React.useEffect(() => {
    const updatePosition = () => {
      if (!containingRef || !containingRef.current) {
        return;
      }

      const rect = containingRef.current.getBoundingClientRect();

      // Add two pixels to better center menu in row
      const newTop = rect.top + (window.scrollY || window.pageYOffset) + 2;
      if (newTop !== top) {
        setTop(newTop);
      }

      const newLeft = rect.left + (window.scrollX || window.pageXOffset);
      if (newLeft !== left) {
        setLeft(newLeft);
      }
    };

    const animatePosition = () => {
      animationFrameId.current = window.requestAnimationFrame(() => animatePosition());
      updatePosition();
    };

    animatePosition();
    return () => {
      if (animationFrameId.current) {
        window.cancelAnimationFrame(animationFrameId.current);
      }
    };
  },
    [ containingRef ] // eslint-disable-line
  );

  if (!containingRef || !node) {
    return null;
  }

  const renderActions = () => {
    if (!node) {
      return null;
    }
    switch (mode) {
      case TreeActionsMode.BUTTON: {
        return <ButtonActions
          node={node}
          onAddOrUploadFiles={onAddOrUploadFiles}
          onDeleteDirectory={onDeleteDirectory}
          onRenameFile={onRenameFile}
          onDeleteFile={onDeleteFile}
        />;
      }
      case TreeActionsMode.MENU: {
        return <MenuActions
          node={node}
          onAddOrUploadFiles={onAddOrUploadFiles}
          onAddDirectory={onAddDirectory}
          onDeleteDirectory={onDeleteDirectory}
          onRenameFile={onRenameFile}
          onRenameDirectory={onRenameDirectory}
          onDeleteFile={onDeleteFile}
        />;
      }
    }
  };

  return ReactDOM.createPortal((
    <div
      className={'tree-actions__portal--' + mode}
      style={{
        'top': top + 'px',
        'left': left + 'px',
      }}
    >
      {renderActions()}
    </div>
  ), document.body);
};
